import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { TicketTable } from '@/components/tickets/TicketTable';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { useToasts } from '@/context/ToastContext';

/**
 * V4.9.2+ — Archived tickets list — same UX fix as DeletedPage.
 * "Unarchive" lives inline on each row, no more bottom button grid.
 */
export function ArchivesPage() {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const query = useQuery({ queryKey: ['archives'], queryFn: () => ticketsApi.listArchived() });
  const unarchive = useMutation({
    mutationFn: (ticketId: string) => ticketsApi.unarchive(ticketId),
    onSuccess: (_, ticketId) => {
      push({ kind: 'success', title: 'Ticket unarchived', message: `${ticketId} is back in the active list.` });
      void queryClient.invalidateQueries({ queryKey: ['archives'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err: unknown) => {
      push({
        kind: 'error',
        title: 'Unarchive failed',
        message: err instanceof Error ? err.message : 'Try refreshing and retrying.'
      });
    }
  });

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">
          Archived tickets
          {query.data ? <span className="text-muted text-small"> · {query.data.length}</span> : null}
        </h3>
      </div>
      <div className="card-body no-pad">
        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState error={query.error} onRetry={() => query.refetch()} /> : null}
        {query.data ? (
          <TicketTable
            tickets={query.data}
            emptyLabel="No archived tickets."
            rowActionLabel="Restore"
            rowAction={(ticket) => (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={unarchive.isPending && unarchive.variables === ticket.id}
                onClick={() => {
                  if (window.confirm(`Unarchive ${ticket.id}? It will reappear in the active tickets list.`)) {
                    unarchive.mutate(ticket.id);
                  }
                }}
              >
                {unarchive.isPending && unarchive.variables === ticket.id ? 'Restoring…' : 'Unarchive'}
              </button>
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
